import { formatLifecycleForDocument } from '@ukrop-nm/shared';
import type { DrawingSheetMeta } from './drawingMeta';
import { formatDrawingDateUk } from './drawingMeta';

function escapeHtml(s: string): string {
  return s
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

function cell(v: string): string {
  const t = v.trim();
  return escapeHtml(t || '—');
}

/**
 * A3 landscape sheet with drafting frame + title block (ISO 7200 principles).
 * Topology image is placed in the drawing field on a white print background.
 */
export function composeDrawingSheet(
  topologyJpegDataUrl: string,
  meta: DrawingSheetMeta,
  siteId: string,
  lifecycleRaw?: string | null,
): HTMLElement {
  const lifecycle = formatLifecycleForDocument(lifecycleRaw);
  const root = document.createElement('div');
  root.setAttribute('data-ukrop-drawing-sheet', '1');
  root.innerHTML = `
    <style>
      .ukrop-sheet {
        width: 2480px;
        height: 1754px;
        box-sizing: border-box;
        padding: 36px;
        background: #ffffff;
        color: #111827;
        font: 14px/1.35 "Segoe UI", "Noto Sans", Arial, sans-serif;
      }
      .ukrop-sheet-frame {
        width: 100%;
        height: 100%;
        box-sizing: border-box;
        border: 3px solid #111827;
        display: flex;
        flex-direction: column;
        background: #ffffff;
      }
      .ukrop-sheet-inner {
        flex: 1;
        min-height: 0;
        margin: 6px 6px 0;
        border: 1.5px solid #111827;
        display: flex;
        align-items: stretch;
        justify-content: stretch;
        background: #ffffff;
        overflow: hidden;
      }
      .ukrop-sheet-inner img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        object-position: center;
        background: #ffffff;
        display: block;
      }
      .ukrop-stamp {
        margin: 6px 6px 8px;
        border: 1.5px solid #111827;
        display: grid;
        grid-template-columns: 1.4fr 1.1fr 0.7fr 0.55fr 0.55fr;
        grid-template-rows: auto auto auto auto;
        font-size: 12px;
      }
      .ukrop-stamp > div {
        border-right: 1px solid #111827;
        border-bottom: 1px solid #111827;
        padding: 4px 7px;
        min-height: 28px;
      }
      .ukrop-stamp > div:nth-child(5n) { border-right: none; }
      .ukrop-stamp > div:nth-last-child(-n+5) { border-bottom: none; }
      .ukrop-stamp .lbl {
        display: block;
        font-size: 10px;
        color: #4b5563;
        text-transform: uppercase;
        letter-spacing: 0.03em;
        margin-bottom: 2px;
      }
      .ukrop-stamp .val { font-weight: 650; word-break: break-word; }
      .ukrop-stamp .span2 { grid-column: span 2; }
      .ukrop-stamp .span3 { grid-column: span 3; }
      .ukrop-stamp .span5 { grid-column: span 5; }
      .ukrop-stamp .note { font-size: 11px; color: #374151; font-weight: 500; }
      .ukrop-stamp .std {
        grid-column: span 5;
        border-right: none;
        border-bottom: none;
        font-size: 10px;
        color: #6b7280;
        padding-top: 4px;
        padding-bottom: 4px;
      }
    </style>
    <div class="ukrop-sheet">
      <div class="ukrop-sheet-frame">
        <div class="ukrop-sheet-inner">
          <img alt="Топологія мережі" src="${topologyJpegDataUrl}" />
        </div>
        <div class="ukrop-stamp" role="table" aria-label="Основний напис креслення">
          <div class="span2">
            <span class="lbl">Організація</span>
            <span class="val">${cell(meta.organization)}</span>
          </div>
          <div>
            <span class="lbl">Позначення</span>
            <span class="val">${cell(meta.sheetCode)}</span>
          </div>
          <div>
            <span class="lbl">Лист</span>
            <span class="val">${cell(meta.sheet)}</span>
          </div>
          <div>
            <span class="lbl">Аркушів</span>
            <span class="val">${cell(meta.sheetsTotal)}</span>
          </div>

          <div class="span3">
            <span class="lbl">Найменування</span>
            <span class="val">${cell(meta.drawingTitle)}</span>
          </div>
          <div>
            <span class="lbl">Ревізія</span>
            <span class="val">${cell(meta.revision)}</span>
          </div>
          <div>
            <span class="lbl">Масштаб</span>
            <span class="val">${cell(meta.scale)}</span>
          </div>

          <div class="span2">
            <span class="lbl">Проєкт</span>
            <span class="val">${cell(meta.projectName)}</span>
          </div>
          <div class="span2">
            <span class="lbl">Обʼєкт / майданчик</span>
            <span class="val">${cell(meta.objectName)}</span>
          </div>
          <div>
            <span class="lbl">Site ID</span>
            <span class="val">${cell(siteId)}</span>
          </div>

          <div>
            <span class="lbl">Розробив</span>
            <span class="val">${cell(meta.author)}</span>
          </div>
          <div>
            <span class="lbl">Перевірив</span>
            <span class="val">${cell(meta.checkedBy)}</span>
          </div>
          <div>
            <span class="lbl">Дата</span>
            <span class="val">${cell(formatDrawingDateUk(meta.date))}</span>
          </div>
          <div class="span2">
            <span class="lbl">Примітка</span>
            <span class="val note">${cell(meta.notes)}</span>
          </div>

          <div class="std">
            <strong>Стадія проєкту:</strong> ${escapeHtml(lifecycle.label)} —
            ${escapeHtml(lifecycle.description)}
            <br />
            Креслення мережі · основний напис за принципами ISO 7200.
            Документ підготовлено за принципами TIA-606-C / ISO/IEC 14763-2 / ISO/IEC/IEEE 42010
            (не сертифікація відповідності). Світла тема для друку на білому аркуші.
          </div>
        </div>
      </div>
    </div>
  `;
  return root;
}
